import { useMemo, useState } from "react";
import type {
  CustomerBalance,
  FlowerDay,
  PaymentsTaken,
  SalesSplit,
} from "../api/types";
import {
  ColumnPair,
  Donut,
  IN_COLOUR,
  Kpi,
  InOutBars,
  Key,
  OUT_COLOUR,
  ShareBar,
} from "../components/charts";
import { Card, Empty, Note, PageHead } from "../components/ui";
import { useApi } from "../hooks/useApi";
import type { VendorBalanceRow } from "../hooks/usePickers";
import { addDays, formatMoney, today } from "../lib/format";
import { nameSkeleton } from "../lib/search";

/**
 * The shop in three pictures.
 *
 * <p>Every figure here is already somewhere else in the application, written
 * out as a number in a list. That is exactly the problem this screen is for: a
 * hundred and forty-five balances down a page is a thing you read, and nobody
 * reads it. The same hundred and forty-five as bar lengths is a thing you
 * notice, and what you notice is the one row that is both long and red.
 *
 * <p>Nothing on this screen writes, and none of it is new arithmetic. The
 * customer and grower figures are the same ones the pickers and the balance
 * lists already hold - in a sitting where any of those screens has been opened,
 * this one costs no request at all.
 */

/**
 * The flower a grower is known for, and the colour his bar takes.
 *
 * <p>Colouring thirty-one bars by rank told you nothing you could not already
 * see from their length, and thirty-one different colours would be a rainbow.
 * This says something the chart could not otherwise: who brings what. At a
 * glance the چاندنی men are one colour and the موتیا men another, and whether
 * the shop's supply leans on one flower is suddenly visible.
 *
 * <p>Matched on the consonant skeleton, because the قسم on a grower is typed by
 * hand like the one on a delivery - ڈیلا on five of them and "Dela" on a sixth.
 * Same trick as the customer search and the آیا اور بکا report.
 *
 * <p>Every colour is one the application already spends. Red is not among them
 * and must not be: red here means a customer owes the shop, and a grower's
 * flower is not that.
 */
const FLOWERS = [
  { name: "موتیا", colour: "#0d5c4d" },
  { name: "چاندنی", colour: "#a8823c" },
  { name: "ڈیلا", colour: "#8f3d22" },
  { name: "گیندہ", colour: "#3f8574" },
  { name: "گلاب", colour: "#d8a858" },
].map((flower) => ({ ...flower, bones: nameSkeleton(flower.name) }));

/** A grower whose قسم is blank, or is something nobody else brings. */
const OTHER = { name: "دیگر", colour: "#cdd4db", bones: "" };

/** Which of the flowers above this grower's قسم is, or دیگر. */
function flowerOf(qism: string | undefined) {
  const bones = nameSkeleton(qism ?? "");
  if (!bones) return OTHER;
  return FLOWERS.find((flower) => flower.bones === bones) ?? OTHER;
}

/** How many customers a column chart can carry without becoming a picket fence. */
const TOP_CUSTOMERS = 10;

/**
 * Who counts as "taking goods and not paying".
 *
 * <p>Half, because it has to be a line somebody can repeat: this man has handed
 * back less than half of what he has taken. Not a guess at lateness - payments
 * in this book are not tied to particular bills, so there is no honest way to
 * say a given bill is forty days overdue, and inventing one would be worse than
 * the plain fact that half the money has not come back.
 */
const SLOW_PAYER = 0.5;

/** The spans the flower picture is asked for, in taps. */
const SPANS = [
  ["آج", 0],
  ["7 دن", 6],
  ["30 دن", 29],
] as const;

export function Dashboard() {
  const [back, setBack] = useState<number>(0);
  const [who, setWho] = useState<"big" | "slow">("big");
  const from = addDays(today(), -back);
  const to = today();

  /*
   * The same two lists every picker on every screen is already holding, asked
   * for by the same addresses so the cache answers both instantly. This screen
   * deliberately adds no endpoint of its own: what it shows is not new, only
   * drawn.
   */
  const customers = useApi<CustomerBalance[]>(
    "/customers/balances?includeSettled=true",
    [],
    {
      cache: true,
    },
  );
  const vendors = useApi<VendorBalanceRow[]>("/vendors/balances", [], {
    cache: true,
  });
  const flowers = useApi<FlowerDay[]>(
    `/reports/flowers?from=${from}&to=${to}`,
    [from, to],
  );

  /*
   * Asked of the server rather than worked out here. The alternative is
   * fetching a month of sales to the phone to add up two figures, which is the
   * fifteen hundred rows this screen exists to avoid making anybody wait for.
   */
  const split = useApi<SalesSplit>(
    `/reports/sales-split?from=${from}&to=${to}`,
    [from, to],
  );
  const taken = useApi<PaymentsTaken>(
    `/reports/payments-taken?from=${from}&to=${to}`,
    [from, to],
  );

  /*
   * The same length of time, immediately before.
   *
   * <p>A figure on its own says nothing - 21.9 لاکھ is only large or small
   * against what the month before did. Two more reads, both of them a single
   * summed row from the database.
   */
  const span = back + 1;
  const wasFrom = addDays(from, -span);
  const wasTo = addDays(from, -1);
  const wasSplit = useApi<SalesSplit>(
    `/reports/sales-split?from=${wasFrom}&to=${wasTo}`,
    [wasFrom, wasTo],
  );
  const wasTaken = useApi<PaymentsTaken>(
    `/reports/payments-taken?from=${wasFrom}&to=${wasTo}`,
    [wasFrom, wasTo],
  );

  /*
   * Ranked by what each man has BOUGHT, not by what he owes.
   *
   * <p>The balances page already answers "who owes the most". This answers a
   * different question - who the shop's business actually rests on - and the
   * two together are the point: a customer near the top of both is where the
   * money is, and where the risk is, at the same time.
   */
  const customerColumns = useMemo(() => {
    const all = (customers.data ?? [])
      .map((row) => ({
        name: row.customer.name,
        took: Number(row.totals.totalSale),
        gave: Number(row.totals.totalPaid),
      }))
      .filter((row) => row.took > 0);

    const rows =
      who === "slow"
        ? all
            .filter(
              (row) =>
                row.gave / row.took < SLOW_PAYER && row.took - row.gave > 0,
            )
            // Ranked by what is actually outstanding, not by the ratio: a man
            // who owes two lakh matters more than one who owes two thousand,
            // whatever share either of them has paid.
            .sort((a, b) => b.took - b.gave - (a.took - a.gave))
            .slice(0, TOP_CUSTOMERS)
        : all.sort((a, b) => b.took - a.took).slice(0, TOP_CUSTOMERS);

    const took = rows.reduce((sum, row) => sum + row.took, 0);
    const gave = rows.reduce((sum, row) => sum + row.gave, 0);

    return {
      rows,
      took,
      gave,
      tallest: rows.reduce((max, row) => Math.max(max, row.took, row.gave), 0),
      slowCount: all.filter(
        (row) => row.gave / row.took < SLOW_PAYER && row.took - row.gave > 0,
      ).length,
    };
  }, [customers.data, who]);

  /*
   * Five growers by name and the remaining however-many as one grey slice.
   *
   * <p>The grey one is not padding. If it is the biggest slice the shop depends
   * on nobody in particular, which is a comfortable place to be; the day it
   * shrinks and one named slice swells, that is worth seeing before the grower
   * in question notices it too.
   */
  const supply = useMemo(() => {
    const rows = (vendors.data ?? [])
      .map((row) => ({
        name: row.name,
        amount: Number(row.totalGoods),
        qism: row.qism,
      }))
      .filter((row) => row.amount > 0)
      .sort((a, b) => b.amount - a.amount);

    const total = rows.reduce((sum, row) => sum + row.amount, 0);

    /*
     * Every grower, as a ranked column.
     *
     * <p>There was a ring above this and it has gone. It named five growers and
     * showed the other twenty-six as one grey lump; these bars name all
     * thirty-one and give each an exact share, so everything the ring said is
     * said better here. What the ring was really for - is anybody too big - is
     * now the line of text above, which is the whole of its content in one
     * sentence.
     */
    const ranked = rows.map((row) => {
      const flower = flowerOf(row.qism);
      return { ...row, colour: flower.colour, flower: flower.name };
    });

    /*
     * Only the flowers actually on the chart get a square, biggest first by
     * what that flower brings in - not by where its first grower happens to
     * rank, which put دیگر in the middle of the row and read like an accident.
     */
    const byFlower = new Map<string, number>();
    for (const row of ranked) {
      byFlower.set(row.flower, (byFlower.get(row.flower) ?? 0) + row.amount);
    }

    const key = [...byFlower.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => ({
        label: name,
        colour:
          [...FLOWERS, OTHER].find((flower) => flower.name === name)?.colour ??
          OTHER.colour,
      }));

    return {
      ranked,
      key,
      total,
      count: rows.length,
      widest: rows[0]?.amount ?? 0,
      top: rows[0],
    };
  }, [vendors.data]);

  /*
   * Only the flowers that actually arrived in these days.
   *
   * <p>Two reasons, and the second one is the one that forced it. A row with
   * nothing on the delivery side has no comparison in it - that is the whole
   * question this picture asks - and کیش and خرچہ are not flowers at all. They
   * are cash and expenses written as items, so their "weight" is a count of
   * rupees: کیش alone came to 6,830 against موتیا's 2,501, took the top of the
   * scale, and squashed every real flower into a stub.
   *
   * <p>Not a silent filter. The line under the heading says what is being
   * shown, because a reader who knows سوکامل sold today and cannot see it must
   * be told why rather than left to wonder.
   */
  /*
   * Two slices, which is the one thing a ring is better at than a column.
   *
   * <p>Cash takes the deep shade and اُدھار the pale one, deliberately that way
   * round: the dark part is the money actually in hand, and on a month that ran
   * at three percent cash it is a thin dark sliver on a pale ring, which is the
   * sentence. Not red - اُدھار is how this trade works, and red here is kept for
   * one customer owing one shop.
   */
  const splitSlices = useMemo(() => {
    const cash = Number(split.data?.cash ?? 0);
    const credit = Number(split.data?.credit ?? 0);
    const total = cash + credit;

    return {
      total,
      creditPct: total > 0 ? Math.round((credit / total) * 100) : 0,
      slices: [
        { name: "اُدھار", amount: credit, colour: OUT_COLOUR },
        { name: "نقد", amount: cash, colour: IN_COLOUR },
      ],
    };
  }, [split.data]);

  /*
   * What the shop stands at, in four numbers.
   *
   * <p>اُدھار باقی is the only one that is not a span: a debt is a standing
   * thing, so the figure is everything owed today and the movement beside it is
   * how much this span added to it - credit written out, less money taken in.
   * That subtraction is the honest answer to "is the اُدھار pile growing", and
   * the book has no older balance to compare against instead.
   */
  const headline = useMemo(() => {
    const sold =
      Number(split.data?.cash ?? 0) + Number(split.data?.credit ?? 0);
    const soldBefore =
      Number(wasSplit.data?.cash ?? 0) + Number(wasSplit.data?.credit ?? 0);
    const paid = Number(taken.data?.total ?? 0);
    const paidBefore = Number(wasTaken.data?.total ?? 0);

    const owed = (customers.data ?? []).reduce(
      (sum, row) => sum + Math.max(Number(row.totals.balance), 0),
      0,
    );
    const owedMoved = Number(split.data?.credit ?? 0) - paid;

    return {
      sold,
      soldMoved: soldBefore > 0 ? sold - soldBefore : undefined,
      paid,
      paidMoved: paidBefore > 0 ? paid - paidBefore : undefined,
      owed,
      owedMoved,
      lent: (vendors.data ?? []).reduce(
        (sum, row) => sum + Number(row.totalAdvance),
        0,
      ),
      lentTo: (vendors.data ?? []).filter((row) => Number(row.totalAdvance) > 0)
        .length,
    };
  }, [
    split.data,
    wasSplit.data,
    taken.data,
    wasTaken.data,
    customers.data,
    vendors.data,
  ]);

  const flowerRows = useMemo(() => {
    const rows = (flowers.data ?? [])
      // Deliveries with no قسم typed on them belong on the آیا اور بکا page,
      // where they can be found and put right. On a summary they are a row
      // called "(قسم نہیں لکھی)" that answers nothing and is read every time.
      .filter((row) => Number(row.came) > 0 && !row.name.startsWith("("))
      .slice(0, 6);
    const widest = rows.reduce(
      (max, row) => Math.max(max, Number(row.came), Number(row.sold)),
      0,
    );
    return { rows, widest };
  }, [flowers.data]);

  const percent = (amount: number) =>
    supply.total > 0 ? `${((amount / supply.total) * 100).toFixed(1)}%` : "—";

  return (
    <>
      <PageHead
        title="کاروبار کی تصویر"
        hint="ایک نظر میں — گاہک، زمیندار اور پھول۔"
      />

      {/* One control for the whole screen. Two cards below carry dates and two
          are all-time; each says which it is rather than leaving it to be
          guessed from whether a number moved. */}
      <Card>
        <div className="btn-row">
          {SPANS.map(([label, days]) => (
            <button
              key={label}
              type="button"
              className={`btn btn--small ${back === days ? "" : "btn--ghost"}`}
              onClick={() => setBack(days)}
            >
              {label}
            </button>
          ))}
        </div>
      </Card>

      <div className="kpis">
        <Kpi
          hero
          label="کل بکری"
          value={headline.sold}
          change={headline.soldMoved}
          changeLabel="پچھلے عرصے سے"
          good="up"
        />
        <Kpi
          label="کل ادائیگی"
          value={headline.paid}
          change={headline.paidMoved}
          changeLabel="پچھلے عرصے سے"
          good="up"
        />
        <Kpi
          label="اُدھار باقی"
          value={headline.owed}
          change={headline.owedMoved}
          changeLabel="اس عرصے میں"
          good="down"
        />
        <Kpi
          label="ایڈوانس باہر"
          value={headline.lent}
          context={`${headline.lentTo} زمینداروں کے پاس`}
        />
      </div>

      <div className="board">
        <Card title="نقد اور اُدھار">
          <p className="hint hint--lead">
            اِن دنوں میں کتنا مال ہاتھ کے ہاتھ بکا۔
          </p>

          {split.error && <Note kind="error">{split.error}</Note>}

          {splitSlices.total > 0 ? (
            <div className="supply">
              <Donut
                slices={splitSlices.slices}
                middle={`${splitSlices.creditPct}%`}
                under="اُدھار"
              />

              <ul className="supply__list">
                {splitSlices.slices.map((slice) => (
                  <li key={slice.name}>
                    <i
                      style={{ background: slice.colour }}
                      aria-hidden="true"
                    />
                    <span>{slice.name}</span>
                    <b>{formatMoney(slice.amount)}</b>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            !split.loading && <Empty>اِن دنوں میں کچھ نہیں بکا۔</Empty>
          )}
        </Card>

        {customers.error && <Note kind="error">{customers.error}</Note>}

        <Card title="گاہک — لیا اور دیا">
          <div className="btn-row">
            <button
              type="button"
              className={`btn btn--small ${who === "big" ? "" : "btn--ghost"}`}
              onClick={() => setWho("big")}
            >
              سب سے بڑے 10
            </button>
            <button
              type="button"
              className={`btn btn--small ${who === "slow" ? "" : "btn--ghost"}`}
              onClick={() => setWho("slow")}
            >
              جو نہیں دے رہے ({customerColumns.slowCount})
            </button>
          </div>

          {/* The two figures said out loud, because the columns show the shape
              of the gap and not its size. */}
          {customerColumns.rows.length > 0 && (
            <p className="compare">
              اِن {customerColumns.rows.length} گاہکوں نے{" "}
              <b className="is-took">{formatMoney(customerColumns.took)}</b> کا
              مال لیا، اور{" "}
              <b className="is-gave">{formatMoney(customerColumns.gave)}</b>{" "}
              واپس دیا۔ باقی{" "}
              <b className="is-took">
                {formatMoney(customerColumns.took - customerColumns.gave)}
              </b>
              ۔
            </p>
          )}

          {/* Ten pairs will not fit across a phone, so the plot scrolls sideways
              rather than squeezing twenty columns into three hundred pixels. */}
          <div className="pairs-wrap">
            <div className="pairs">
              {customerColumns.rows.map((row) => (
                <ColumnPair
                  key={row.name}
                  {...row}
                  tallest={customerColumns.tallest}
                />
              ))}
            </div>
          </div>

          {customerColumns.rows.length === 0 && !customers.loading && (
            <Empty>
              {who === "slow"
                ? "ہر گاہک نے آدھے سے زیادہ دے رکھا ہے۔"
                : "ابھی کسی گاہک کا حساب نہیں بنا۔"}
            </Empty>
          )}

          {customerColumns.rows.length > 0 && (
            <Key
              items={[
                { label: "اُدھار لیا", colour: "var(--red)" },
                { label: "ادائیگی دی", colour: "var(--green)" },
              ]}
            />
          )}
        </Card>

        <Card title="مال کس زمیندار کا آ رہا ہے">
          <p className="hint hint--lead">
            کل {formatMoney(supply.total)} روپے کا مال، اب تک۔
          </p>

          {supply.ranked.length > 0 ? (
            <>
              <p className="headline">
                <b>{supply.count}</b> زمیندار — سب سے بڑا {supply.top?.name}،{" "}
                {percent(supply.top?.amount ?? 0)}
              </p>

              {supply.ranked.map((row) => (
                <ShareBar
                  key={row.name}
                  name={row.name}
                  amount={row.amount}
                  colour={row.colour}
                  widest={supply.widest}
                  total={supply.total}
                />
              ))}

              <Key items={supply.key} />
            </>
          ) : (
            !vendors.loading && <Empty>ابھی کسی زمیندار کا مال نہیں آیا۔</Empty>
          )}
        </Card>

        <Card title="آیا اور بکا">
          {/* Two sentences of apparatus on a summary card is more than it can
              carry. The unit has to be said; which flowers are in and where the
              full list lives are things a reader learns once. */}
          <p className="hint hint--lead">
            وزن <b>کلو</b> میں
          </p>

          {flowers.error && <Note kind="error">{flowers.error}</Note>}

          <div className="inout-list">
            {flowerRows.rows.map((row) => (
              <InOutBars
                key={row.name}
                name={row.name}
                came={Number(row.came)}
                sold={Number(row.sold)}
                widest={flowerRows.widest}
              />
            ))}
          </div>

          {flowerRows.rows.length === 0 && !flowers.loading && (
            <Empty>اِن دنوں میں کسی زمیندار کا مال نہیں آیا۔</Empty>
          )}

          {flowerRows.rows.length > 0 && (
            <Key
              items={[
                { label: "آیا (کلو)", colour: IN_COLOUR },
                { label: "بکا (کلو)", colour: OUT_COLOUR },
              ]}
            />
          )}
        </Card>
      </div>
    </>
  );
}
